import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { File02 } from '@/components/common/icons';
import { Text } from '@/components/common/Typography';
import { openInNewTab } from '@/util/url';
import { FlexProps } from '@chakra-ui/react';
import { Goal } from '@common/goals';
import { GoalType } from '@common/graphql/generated';
import { toPx } from '@common/util/styles';
import babyImage from '/public/images/GoalOnboardingIntroHeader/baby.png';
import carImage from '/public/images/GoalOnboardingIntroHeader/car.png';
import cashManagementImage from '/public/images/GoalOnboardingIntroHeader/cash_management.png';
import customImage from '/public/images/GoalOnboardingIntroHeader/custom.png';
import daycareImage from '/public/images/GoalOnboardingIntroHeader/daycare.png';
import emergencyFundImage from '/public/images/GoalOnboardingIntroHeader/emergency_fund.png';
import generalInvestingImage from '/public/images/GoalOnboardingIntroHeader/general_investing.png';
import homeDownPaymentImage from '/public/images/GoalOnboardingIntroHeader/home_down_payment.png';
import retirementImage from '/public/images/GoalOnboardingIntroHeader/retirement.png';
import vacationImage from '/public/images/GoalOnboardingIntroHeader/vacation.png';
import weddingImage from '/public/images/GoalOnboardingIntroHeader/wedding.png';
import Image, { StaticImageData } from 'next/image';
import { configByGoalType } from '../../config';
import { GoalIconBase } from '../../GoalIcon';

export const imageByGoalType: Record<GoalType, StaticImageData> = {
    CASH_MANAGEMENT: cashManagementImage,
    GENERAL_INVESTING: generalInvestingImage,
    RETIREMENT: retirementImage,
    HOME_DOWN_PAYMENT: homeDownPaymentImage,
    EMERGENCY_FUND: emergencyFundImage,
    PAY_OFF_DEBT: customImage, // TODO: Replace this when goal is implemented
    VACATION: vacationImage,
    COLLEGE: customImage, // TODO: Replace this when goal is implemented
    CAR: carImage,
    WEDDING: weddingImage,
    BABY: babyImage,
    DAYCARE: daycareImage,
    CUSTOM: customImage,
};

export const messageByGoalType: Record<GoalType, string> = {
    CASH_MANAGEMENT: 'A safe place for your cash to grow',
    GENERAL_INVESTING: 'The simple way to grow your wealth',
    RETIREMENT: 'Invest today & enjoy tomorrow with retirement peace of mind',
    HOME_DOWN_PAYMENT:
        'Get one step closer to your future dream home right now',
    EMERGENCY_FUND: 'Save for life’s little surprises',
    PAY_OFF_DEBT: '', // TODO: Populate this when goal is implemented
    VACATION: 'Let’s make your dream vacation a reality',
    COLLEGE: '', // TODO: Populate this when goal is implemented
    CAR: 'Drive toward your dream car with smart investing',
    WEDDING: 'Put money away to make your wedding day perfect',
    BABY: 'Start planning for your growing family',
    DAYCARE: 'Invest in your kids, get ready for daycare',
    CUSTOM: 'Create your own goals for anything important to you',
};

interface GoalOnboardingIntroHeaderProps extends FlexProps {
    goalType: GoalType;
    goal?: Goal;
}

const GoalOnboardingIntroHeader = ({
    goalType,
    goal,
    ...props
}: GoalOnboardingIntroHeaderProps) => {
    const goalConfig = configByGoalType[goalType];
    const image = imageByGoalType[goalType];
    const title = goal?.name ?? goalConfig.defaultName;
    const message = messageByGoalType[goalType];
    const blogLink = goalConfig.blogLink;

    return (
        <FadeInBox
            w="full"
            h={{ base: 320, '2xl': 380 }}
            position="relative"
            {...props}
        >
            <Image
                src={image.src}
                alt={title}
                fill
                objectFit="cover"
                placeholder="blur"
                blurDataURL={image.blurDataURL}
            />
            <FlexRow
                position="absolute"
                left={50}
                w={365}
                h={230}
                zIndex={100}
                bgColor={goalConfig.color}
            >
                <FlexCol px={8} py={8} justifyContent="space-between" w="full">
                    <FlexRow align="center" gap={3}>
                        <GoalIconBase
                            type={goalType}
                            progress={0}
                            size="xl"
                            inverted
                        />
                        <Text fontWeight="semibold" color="white">
                            {title}
                        </Text>
                    </FlexRow>
                    <Text fontSize="3xl" color="white">
                        {message}
                    </Text>
                </FlexCol>
            </FlexRow>
            {blogLink && (
                <FlexRow
                    position="absolute"
                    top={toPx(20)}
                    right={toPx(50)}
                    zIndex={100}
                    color="textMuted"
                >
                    <ButtonWithIcon
                        size="sm"
                        variant="pill"
                        colorScheme="white"
                        iconLeft={<File02 />}
                        onClick={() => {
                            openInNewTab(blogLink.url);
                        }}
                        onClickEvent="Blog Link Clicked"
                        onClickEventParams={{
                            location: 'Goal Onboarding Intro Header',
                            label: blogLink.description,
                        }}
                    >
                        {blogLink.description}
                    </ButtonWithIcon>
                </FlexRow>
            )}
        </FadeInBox>
    );
};

export default GoalOnboardingIntroHeader;
